import React, { useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  Plus, ArrowUp, FileText, X, Quote, BookOpen, AtSign, MessageSquare, Play, Command, Square, ListTodo,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FileTypeIcon } from "@/shared/components/icons/filetype";
import PlusMenu from "./PlusMenu";
import type { Agent, Attachment, Mention, MentionType } from "@/shared/types";

/* Compact removable chip shown inside the composer before sending */
function ComposerChip({ file, onRemove }: { file: Attachment; onRemove?: () => void }) {
  return (
    <div className="flex items-center gap-2 h-9 rounded-[10px] bg-[var(--surface-sunk)] border border-[var(--hairline)] pl-1.5 pr-1 min-w-0 max-w-[220px]">
      {file.kind === "image" && file.previewSrc ? (
        <img src={file.previewSrc} alt="" className="w-6 h-6 rounded-[6px] object-cover shrink-0" />
      ) : (
        // @ts-expect-error TODO(types): FileTypeIcon declares no `radius` prop — dead prop, silently dropped (SMELLS.md #34); frozen under the behaviour freeze.
        <FileTypeIcon kind={file.kind} size={24} radius={6} />
      )}
      <span className="text-[11.5px] text-[var(--text-secondary)] truncate">{file.name}</span>
      <button onClick={onRemove} aria-label={`Remove ${file.name}`} title={`Remove ${file.name}`} className="press w-5 h-5 shrink-0 rounded-full hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-tertiary)]">
        <X className="w-3 h-3" strokeWidth={1.75} />
      </button>
    </div>
  );
}

/* ---------- @-mentions: tag tasks, chats, artifacts ---------- */

/* Keyed by `MentionItem["type"]`; `Record<string, …>` because MentionChip
   accepts an open string from callers outside the picker. */
const MENTION_META: Record<string, { icon: LucideIcon; label: string; tint: string }> = {
  task: { icon: ListTodo, label: "Tasks", tint: "text-[var(--info-text)]" },
  kb: { icon: BookOpen, label: "Knowledge", tint: "text-[var(--success)]" },
  chat: { icon: MessageSquare, label: "Chats", tint: "text-[var(--text-secondary)]" },
  artifact: { icon: FileText, label: "Artifacts", tint: "text-[var(--text-secondary)]" },
  command: { icon: Play, label: "Commands", tint: "text-[var(--brand)]" },
};

/** A row in the @-mention or slash-command menu. `send` is carried by slash
    commands only — picking one sends that text straight away. */
interface MentionItem {
  id: string;
  type: MentionType;
  name: string;
  hint: string;
  emoji?: string;
  send?: string;
}

type SlashCommand = MentionItem & { send: string };

/* Everything on this agent that can be @-tagged, flattened for filtering.
   No Knowledge entry: a chat's bases are set in the right panel, and a file
   count here would need a query per keystroke. */
const mentionables = (agent?: Agent | null): MentionItem[] => [
  ...(agent?.tasks || []).map((t) => ({ id: t.id, type: "task" as const, name: t.name, hint: t.schedule })),
  ...(agent?.chats || []).map((c) => ({ id: `chat-${c.id}`, type: "chat" as const, name: c.title, hint: c.updatedAt })),
];

/* Slash commands: quick actions typed as "/..." at the start of an empty draft.
   Picking one sends immediately (Slack-style). */
const slashCommands = (agent?: Agent | null): SlashCommand[] => [
  ...(agent?.tasks || []).map((t) => ({ id: `run-t-${t.id}`, type: "command" as const, name: `Run ${t.name}`, hint: "task", send: `/run ${t.name}` })),
  { id: "cmd-summarize", type: "command" as const, name: "Summarize this chat", hint: "/summarize", send: "/summarize" },
  { id: "cmd-status", type: "command" as const, name: "Status update", hint: "/status", send: "/status" },
];

/* Active "@query" token behind the caret, or null. Word-boundary before the @,
   dies on newline so a stray @ earlier in the draft doesn't reopen the menu. */
const mentionCtx = (val: string, caret: number) => {
  const upto = val.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const q = upto.slice(at + 1);
  if (/\n/.test(q) || q.length > 32) return null;
  return { at, q };
};

export function MentionChip({ m, onRemove }: {
  m: { type: string; name: string; emoji?: string };
  onRemove?: () => void;
}) {
  const meta = MENTION_META[m.type];
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-1.5 h-7 rounded-full bg-[var(--surface-sunk)] border border-[var(--hairline)] pl-2 pr-1 min-w-0 max-w-[220px]">
      {m.emoji ? <span className="text-[12px] leading-none shrink-0">{m.emoji}</span> : <Icon className={`w-3.5 h-3.5 shrink-0 ${meta.tint}`} strokeWidth={1.75} />}
      <span className="text-[12px] text-[var(--text-secondary)] truncate">{m.name}</span>
      {onRemove && (
        <button onClick={onRemove} aria-label={`Remove ${m.name}`} title={`Remove ${m.name}`} className="press w-[18px] h-[18px] shrink-0 rounded-full hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-tertiary)]">
          <X className="w-3 h-3" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

/* Popover above the composer. `items` is the flat filtered list; grouped
   headers are derived from consecutive runs of the same type. */
function MentionMenu({ items, sel, onPick, onHover, query, slash = false }: {
  items: MentionItem[];
  sel: number;
  onPick: (it: MentionItem) => void;
  onHover: (i: number) => void;
  query?: string | null;
  slash?: boolean;
}) {
  const selRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selRef.current?.scrollIntoView({ block: "nearest" });
  }, [sel]);
  const HeaderIcon = slash ? Command : AtSign;
  return (
    <div className="absolute bottom-full inset-x-0 mb-2 z-40 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-menu overflow-hidden fade-in-up">
      <div className="flex items-center gap-1.5 px-3 h-8 border-b hairline bg-[var(--surface-sunk)]/70">
        <HeaderIcon className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
        <span className="text-[11px] text-[var(--text-tertiary)] tracking-[-0.005em]">
          {query
            ? <>Matching “{query}”</>
            : slash
              ? "Run a command"
              : "Tag a task, chat, or artifact"}
        </span>
      </div>
      {/* Ceiling: 188px keeps the menu inside the empty-state's overflow-hidden hero; bump if item lists grow */}
      <div className="max-h-[188px] overflow-y-auto p-1">
        {items.map((it, i) => {
          const meta = MENTION_META[it.type];
          const Icon = meta.icon;
          const header = !slash && (i === 0 || items[i - 1].type !== it.type);
          return (
            <React.Fragment key={it.id}>
              {header && <div className="section-label px-2 pt-1.5 pb-0.5">{meta.label}</div>}
              <button
                ref={i === sel ? selRef : null}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => onHover(i)}
                onClick={() => onPick(it)}
                className={`w-full flex items-center gap-2.5 px-2 py-[7px] rounded-[9px] text-left min-w-0 ${
                  i === sel ? "bg-[var(--accent-hover)]" : ""
                }`}
              >
                {it.emoji ? (
                  <span className="text-[14px] leading-none shrink-0 w-4 text-center">{it.emoji}</span>
                ) : (
                  <Icon className={`w-4 h-4 shrink-0 ${meta.tint}`} strokeWidth={1.75} />
                )}
                <span className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em] truncate">{it.name}</span>
                <span className="ml-auto shrink-0 text-[11px] text-[var(--text-tertiary)] font-mono">{it.hint}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function Composer({
  onSend, agent, live = false, onStop,
  attachments = [], onAddFiles, onRemoveAttachment, quote, onClearQuote, strip,
  placeholder, text, onTextChange: setText, mentions, onMentionsChange: setMentions, autoFocus = false, onNewAgent,
  newAgentHint = "with Builder",
}: {
  /** What the box holds is the chat's draft (`useChatDraft`): sending takes it from there. */
  onSend: (text: string, mentions?: Mention[]) => void;
  agent?: Agent | null;
  /** A turn is in flight: Send becomes Stop. */
  live?: boolean;
  onStop?: () => void;
  attachments?: Attachment[];
  onAddFiles?: (files: FileList | null) => void;
  onRemoveAttachment?: (i: number) => void;
  quote?: string | null;
  onClearQuote?: () => void;
  /** The tray's strip under the card, its children spread end to end — an agent's setup box and
      knowledge, or Builder's agent picker. */
  strip?: React.ReactNode;
  /** Left out: "Message <agent>…". */
  placeholder?: string;
  text: string;
  onTextChange: Dispatch<SetStateAction<string>>;
  mentions: Mention[];
  onMentionsChange: Dispatch<SetStateAction<Mention[]>>;
  /** A draft carried over from another chat (New agent): the box opens focused on it. */
  autoFocus?: boolean;
  /** + offers New agent too, handed the draft. */
  onNewAgent?: (draft: string) => void;
  newAgentHint?: string;
}) {
  const [focused, setFocused] = useState(false);
  const [mention, setMention] = useState<{ at: number; q: string } | null>(null); // { at, q } while an @token is live
  const [mentionSel, setMentionSel] = useState(0);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const taken = new Set(mentions.map((m) => m.id));
  const mentionItems = mention
    ? mentionables(agent).filter(
        (it) => !taken.has(it.id) && it.name.toLowerCase().includes(mention.q.toLowerCase())
      )
    : [];
  const menuOpen = mention && mentionItems.length > 0;

  // Slash commands: draft starts with "/" and stays on one line.
  const [slashDismissed, setSlashDismissed] = useState(false);
  const slashQ = !mention && !slashDismissed && focused && text.startsWith("/") && !text.includes("\n") ? text.slice(1) : null;
  const slashItems = slashQ != null
    ? slashCommands(agent).filter((c) => c.name.toLowerCase().includes(slashQ.toLowerCase()) || c.send.toLowerCase().startsWith(text.toLowerCase()))
    : [];
  const slashOpen = slashQ != null && slashItems.length > 0;

  const pickSlash = (it: MentionItem) => {
    if (live) return;
    // `!`: only ever called with a slash command, which is what carries `send`.
    onSend(it.send!, []);
  };

  const syncMention = (val: string) => {
    const ctx = mentionCtx(val, taRef.current?.selectionStart ?? val.length);
    setMention(ctx);
    if (!ctx || ctx.q !== mention?.q) setMentionSel(0);
  };

  // `!`: only reachable from the open @-menu, which is what `mention` opens.
  const pickMention = (it: MentionItem) => {
    // Excise the "@query" token the user was typing, keep the rest of the draft.
    const caret = mention!.at + 1 + mention!.q.length;
    setText((t) => t.slice(0, mention!.at) + t.slice(caret));
    setMentions((ms) => [...ms, { id: it.id, type: it.type, name: it.name, emoji: it.emoji }]);
    setMention(null);
    taRef.current?.focus();
  };

  // Autosize on every text change (typing and programmatic clears alike).
  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    const resize = () => {
      el.style.height = "0px";
      // Six 20px lines plus the textarea's 14px top padding.
      el.style.height = Math.min(el.scrollHeight, 134) + "px";
    };
    resize();
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      resize();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const submit = () => {
    const t = text.trim();
    if (live) return; // one turn at a time; the draft waits in the box
    if (!t && attachments.length === 0 && mentions.length === 0) return;
    onSend(t, mentions); // the box empties as the send takes the draft
    setMention(null);
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const navItems = menuOpen ? mentionItems : slashOpen ? slashItems : null;
    if (navItems) {
      const pick = menuOpen ? pickMention : pickSlash;
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionSel((s) => Math.min(s + 1, navItems.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionSel((s) => Math.max(s - 1, 0)); return; }
      if ((e.key === "Enter" && !e.shiftKey) || e.key === "Tab") { e.preventDefault(); pick(navItems[Math.min(mentionSel, navItems.length - 1)]); return; }
      if (e.key === "Escape") { e.preventDefault(); menuOpen ? setMention(null) : setSlashDismissed(true); return; }
    }
    // Ignore Enter while an IME composition is active (CJK input on any OS).
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="relative w-full max-w-[720px] mx-auto min-w-0">
      {menuOpen && (
        <MentionMenu
          items={mentionItems}
          sel={Math.min(mentionSel, mentionItems.length - 1)}
          onPick={pickMention}
          onHover={setMentionSel}
          query={mention!.q}
        />
      )}
      {slashOpen && (
        <MentionMenu
          slash
          items={slashItems}
          sel={Math.min(mentionSel, slashItems.length - 1)}
          onPick={pickSlash}
          onHover={setMentionSel}
          query={slashQ}
        />
      )}
      {/* The tray frames the card on every side and carries the strip under it. */}
      <div className="rounded-[18px] bg-[var(--surface-2)] p-1 min-w-0">
      <div
        className={`rounded-[14px] bg-[var(--surface)] ring-1 transition-[box-shadow] duration-200 [transition-timing-function:var(--easing)] motion-reduce:transition-none min-w-0 ${
          focused
            ? "ring-[var(--hairline-focus)] shadow-[0_1px_2px_rgba(0,0,0,0.05),0_8px_24px_-12px_rgba(0,0,0,0.08)]"
            : "ring-[var(--hairline-strong)] shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
        }`}
      >
        {quote && (
          <div className="flex items-start gap-2 mx-3 mt-3 pl-3 pr-1.5 py-1.5 rounded-[10px] bg-[var(--surface-2)] border-l-2 border-[var(--hairline-focus)] min-w-0">
            <Quote className="w-3 h-3 text-[var(--text-tertiary)] mt-[3px] shrink-0" strokeWidth={1.75} />
            <span className="flex-1 min-w-0 text-[12.5px] text-[var(--text-secondary)] leading-[1.5] line-clamp-2">{quote}</span>
            <button onClick={onClearQuote} aria-label="Remove quote" className="press w-5 h-5 shrink-0 rounded-full hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-tertiary)]">
              <X className="w-3 h-3" strokeWidth={1.75} />
            </button>
          </div>
        )}
        {(attachments.length > 0 || mentions.length > 0) && (
          <div className="flex flex-wrap gap-1.5 mx-3 mt-3">
            {mentions.map((m) => (
              <MentionChip key={m.id} m={m} onRemove={() => setMentions((ms) => ms.filter((x) => x.id !== m.id))} />
            ))}
            {attachments.map((f, i) => (
              <ComposerChip key={i} file={f} onRemove={() => onRemoveAttachment!(i)} />
            ))}
          </div>
        )}
        <textarea
          ref={taRef}
          rows={1}
          autoFocus={autoFocus}
          aria-label={`Message ${agent?.name || "agent"}`}
          value={text}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); setMention(null); }}
          onChange={(e) => { setText(e.target.value); syncMention(e.target.value); setSlashDismissed(false); if (e.target.value.startsWith("/")) setMentionSel(0); }}
          onKeyDown={onKey}
          onKeyUp={(e) => { if (e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End") syncMention((e.target as HTMLTextAreaElement).value); }}
          onClick={(e) => syncMention((e.target as HTMLTextAreaElement).value)}
          placeholder={placeholder ?? `Message ${agent?.name || "agent"}…`}
          className="block w-full min-w-0 resize-none max-h-[134px] overflow-y-auto px-3.5 pt-3.5 bg-transparent outline-none text-[16px] sm:text-[14px] leading-5 text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
        />
        <div className="flex items-center gap-1 mt-[19px] pl-2 pr-2.5 pb-2 min-w-0">
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              onAddFiles?.(e.target.files);
              e.target.value = "";
            }}
          />
          {onNewAgent ? (
            <PlusMenu onAddFiles={() => fileRef.current?.click()} onNewAgent={() => onNewAgent(text.trim())} newAgentHint={newAgentHint} />
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              className="press w-7 h-7 shrink-0 rounded-[8px] hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              title="Attach files"
              aria-label="Attach files"
            >
              <Plus className="w-4 h-4" strokeWidth={1.75} />
            </button>
          )}
          <div className="flex-1" />
          {live ? (
            <button
              onClick={onStop}
              className="press w-[30px] h-[30px] ml-0.5 shrink-0 rounded-full flex items-center justify-center bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)]"
              title="Stop"
              aria-label="Stop"
            >
              <Square className="w-2.5 h-2.5" strokeWidth={2} fill="currentColor" />
            </button>
          ) : (
          <button
            onClick={submit}
            disabled={!text.trim() && attachments.length === 0 && mentions.length === 0}
            className={`press w-[30px] h-[30px] ml-0.5 shrink-0 rounded-full flex items-center justify-center text-[var(--brand-foreground)] ${
              text.trim() || attachments.length > 0 || mentions.length > 0
                ? "bg-[var(--brand)] hover:bg-[var(--brand-hover)]"
                : "bg-[var(--disabled)]"
            }`}
            title="Send"
            aria-label="Send"
          >
            <ArrowUp className="w-4 h-4" strokeWidth={2} />
          </button>
          )}
        </div>
      </div>
      {strip && <div className="h-[37px] flex items-center justify-between gap-2 px-1.5 min-w-0">{strip}</div>}
      </div>
    </div>
  );
}
